/**
 * StatPopup — tap-to-expand stat modal.
 * Web port of client-native/src/features/profile/components/StatPopup.tsx.
 * Copy strings kept EXACT.
 */
import { FC } from "react";
import { Share2 } from "lucide-react";
import { colors } from "@/ui";
import ProfileModal from "./ProfileModal";
import { StatType } from "./ProfileStats";
import { timerIcon, fireIcon, openBookIcon } from "./profileAssets";

interface StatInfo {
  title: string;
  description: string;
  icon: string;
}

const STAT_INFO: Record<StatType, StatInfo> = {
  listening: {
    title: "Listening Time",
    description:
      "This is the total time you've spent listening to Spanish content. The more you listen, the faster you'll improve your comprehension!",
    icon: timerIcon,
  },
  words: {
    title: "Words You Know",
    description:
      "This is the number of Spanish words you've added to your vocabulary. Keep learning new words to expand your fluency!",
    icon: openBookIcon,
  },
  streak: {
    title: "Day Streak",
    description:
      "This is how many days in a row you've practiced. Consistency is key to language learning success!",
    icon: fireIcon,
  },
};

interface StatPopupProps {
  open: boolean;
  statType: StatType | null;
  value: string;
  onClose: () => void;
  onShare: () => void;
  weeksInARow?: number;
}

const StatPopup: FC<StatPopupProps> = ({
  open,
  statType,
  value,
  onClose,
  onShare,
  weeksInARow = 0,
}) => {
  if (!statType) return null;
  const info = STAT_INFO[statType];

  return (
    <ProfileModal open={open} onClose={onClose}>
      <img
        src={info.icon}
        alt=""
        style={{ width: 56, height: 56, marginBottom: 8, objectFit: "contain" }}
      />
      <div className="text-2xl font-bold" style={{ color: colors.primary }}>
        {value}
      </div>
      <div
        className="text-lg font-semibold"
        style={{ color: colors.primary, marginTop: 4 }}
      >
        {info.title}
      </div>
      <p
        className="text-base"
        style={{
          color: colors.secondary,
          textAlign: "center",
          marginTop: 12,
          lineHeight: 1.5,
        }}
      >
        {info.description}
      </p>

      {statType === "streak" && (
        <div
          style={{
            width: "100%",
            backgroundColor: colors.greyLightest,
            borderRadius: 12,
            padding: 16,
            marginTop: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span className="text-base" style={{ color: colors.secondary }}>
              📅 Weeks in a Row
            </span>
            <span
              className="text-base font-bold"
              style={{ color: colors.primary }}
            >
              {weeksInARow} weeks
            </span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onShare}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          backgroundColor: colors.blue,
          color: colors.white,
          padding: "14px 24px",
          borderRadius: 24,
          marginTop: 24,
          border: "none",
          cursor: "pointer",
        }}
      >
        <Share2 size={20} color={colors.white} />
        <span className="text-base font-semibold">Share with a friend</span>
      </button>
    </ProfileModal>
  );
};

export default StatPopup;
